import { ThreeDSecure, ThreeDSecureVerifyPayload } from "braintree-web"
import { useCustomerStore, useGlobalStore, useOrderStore } from "../store/hooks/useStore"
import { useState } from "react"
import { Nullable } from "../types/utils"
import { useBraintree } from "./use-braintree"
import { AddressFormFields } from "../types/forms"
import { CHECKOUT_ERROR_MESSAGES } from "../utils/constants"

interface ThreeDSAuthData {
  nonce: string
  amount: number
  bin: string
  billingAddress: AddressFormFields | undefined
  submit: (payload: ThreeDSecureVerifyPayload) => void
}

export function useBraintreeThreeDS() {
  const { setIsPaymentLoading, setPaymentErrorMessage } = useOrderStore()
  const { customerInfo } = useCustomerStore()
  const {
    getCustomSitePreferenceById,
    isCustomSitePreferenceSet,
    customSitePreferences,
  } = useGlobalStore()
  const { createBraintree3DS } = useBraintree()

  const [threeDS, setThreeDS] = useState<Nullable<ThreeDSecure>>(null)

  const setupSkip3DSecureValidationPref = async () => {
    if (!isCustomSitePreferenceSet('BRAINTREE_3DSecure_Skip_Client_Validation_Result')) {
      await getCustomSitePreferenceById('BRAINTREE_3DSecure_Skip_Client_Validation_Result')
    }
    return (customSitePreferences['BRAINTREE_3DSecure_Skip_Client_Validation_Result'] === 'enabled') as boolean
  }

  const handleThreeDSAuthenticationFailure = (status: string) => {
    setIsPaymentLoading(false)
    const verificationStatus = ['lookup_error', 'lookup_failed_acs_error', 'challenge_required']
    const errorMessage = verificationStatus.includes(status)
      ? CHECKOUT_ERROR_MESSAGES.VERIFICATION_FAILED
      : CHECKOUT_ERROR_MESSAGES.THREEDSECURE_GENERAL
    setPaymentErrorMessage(errorMessage)
  }

  const handleThreeDSCancellation = () => {
    setIsPaymentLoading(false)
    setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.THREEDSECURE_CANCELLED)
  }

  const handleThreeDSAuthentication = async (authData: ThreeDSAuthData) => {

    let threeDSInstance = threeDS

    if (!threeDSInstance) {
      threeDSInstance = await createBraintree3DS()
      setThreeDS(threeDSInstance)
    }
    const {
      nonce,
      amount,
      bin,
      billingAddress,
      submit
    } = authData

    const skip3DSecureValidation = await setupSkip3DSecureValidationPref()

    return threeDSInstance ? threeDSInstance?.verifyCard({
      onLookupComplete: (data, next) => {
        next()
      },
      amount: amount,
      nonce: nonce ?? '',
      bin: bin,
      email: customerInfo?.email,
      billingAddress: {
        givenName: billingAddress?.firstName,
        surname: billingAddress?.lastName,
        phoneNumber: billingAddress?.phone,
        streetAddress: billingAddress?.address1,
        extendedAddress: billingAddress?.address2,
        locality: billingAddress?.city,
        region: billingAddress?.stateCode,
        postalCode: billingAddress?.postalCode,
        countryCodeAlpha2: billingAddress?.countryCode,
      },
    }).then(async (payload) => {
      const threeDSAuthenticateFailed =
        payload?.threeDSecureInfo?.status !== 'authenticate_successful'
      const threeDSCancelled =
        payload.rawCardinalSDKVerificationData?.Payment?.ExtendedData?.ChallengeCancel
      if (threeDSCancelled) {
        handleThreeDSCancellation()
      } else if (threeDSAuthenticateFailed && !skip3DSecureValidation) {
        handleThreeDSAuthenticationFailure(payload?.threeDSecureInfo?.status)
      } else {
        submit(payload)
      }
    }).catch((error) => {
      console.error(error)
      if (error.code == 'THREEDS_CARDINAL_SDK_ERROR') {
        setIsPaymentLoading(false)
        setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.THREEDSECURE_GENERAL)
      }
    }) : null
  }

  return {
    handleThreeDSAuthentication
  }
}